import {
  REQUEST_ID_HEADER,
  type AuditEventResource,
  type CatalogSummaryResource,
  type CustomerResource,
  type EnvironmentResource,
  type LicensedProductPlanResource,
  type HealthResponse,
  type InvitationResource,
  type MemberResource,
  type MeResponse,
  type OrganizationResource,
  type Page,
  type PlatformInfoResponse,
  type PlatformUserResource,
  type ProductResource,
  type Problem,
} from '@verydia/contracts';
import { VerydiaApiError, VerydiaConnectionError } from './errors.js';

/**
 * The Verydia Control TypeScript client.
 *
 * VC-0 covers the two endpoints that exist. The shape is what matters: transport
 * concerns (retries, timeouts, request-id capture, Problem Details decoding) are
 * solved once here so that every endpoint added in VC-2+ inherits them instead of
 * each one growing its own error handling.
 *
 * `fetch` is injected so the client runs unchanged on Node, in a browser, in a
 * Lambda and inside tests, with no polyfill and no HTTP-library dependency.
 */
export interface VerydiaClientOptions {
  baseUrl: string;
  /** Management API credential (`vc_live_…` / `vc_test_…`). Omit for public routes. */
  apiKey?: string;
  /**
   * Supplies a bearer token per request, for callers authenticating as a person
   * rather than as a machine.
   *
   * A function rather than a string because an access token expires: the console
   * refreshes its own, and a token captured once at construction would be stale
   * within the hour. Returning null sends the request unauthenticated, which is
   * correct for the public endpoints.
   */
  authorization?: () => Promise<string | null>;
  fetch?: typeof globalThis.fetch;
  /** Per-attempt timeout. Total wall time may reach this × (maxRetries + 1). */
  timeoutMs?: number;
  /** Retries for 429/5xx and network errors. Applies to safe methods only. */
  maxRetries?: number;
  /** Injected for deterministic tests. */
  sleep?: (ms: number) => Promise<void>;
  userAgent?: string;
}

export interface RequestResult<T> {
  data: T;
  /** The server-assigned request identifier. Quote this in support tickets. */
  requestId: string | null;
  status: number;
}

/**
 * Path segments are encoded, even though every id this API issues is
 * `prefix_` plus Crockford base32 and therefore URL-safe already. The one time
 * that stops being true is the one time an id arrives from somewhere else.
 */
function encode(segment: string): string {
  return encodeURIComponent(segment);
}

const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_MAX_RETRIES = 2;

export class VerydiaControlClient {
  readonly #authorization: (() => Promise<string | null>) | undefined;
  readonly #baseUrl: string;
  readonly #apiKey: string | undefined;
  readonly #fetch: typeof globalThis.fetch;
  readonly #timeoutMs: number;
  readonly #maxRetries: number;
  readonly #sleep: (ms: number) => Promise<void>;
  readonly #userAgent: string;

  constructor(options: VerydiaClientOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.#apiKey = options.apiKey;
    this.#authorization = options.authorization;
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.#maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.#sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    this.#userAgent = options.userAgent ?? 'verydia-control-sdk-js/0.1.0';
  }

  health(): Promise<RequestResult<HealthResponse>> {
    return this.#request<HealthResponse>('GET', '/health');
  }

  platformInfo(): Promise<RequestResult<PlatformInfoResponse>> {
    return this.#request<PlatformInfoResponse>('GET', '/v1/platform/info');
  }

  /* --------------------------------- identity ------------------------------ */

  /** Who the bearer token belongs to, and what they can reach. */
  me(): Promise<RequestResult<MeResponse>> {
    return this.#request<MeResponse>('GET', '/v1/me');
  }

  listOrganizations(): Promise<RequestResult<Page<OrganizationResource>>> {
    return this.#request<Page<OrganizationResource>>('GET', '/v1/platform/organizations');
  }

  createOrganization(input: {
    display_name: string;
    slug?: string;
  }): Promise<RequestResult<OrganizationResource>> {
    return this.#request<OrganizationResource>('POST', '/v1/platform/organizations', input);
  }

  getPlatformOrganization(id: string): Promise<RequestResult<OrganizationResource>> {
    return this.#request<OrganizationResource>('GET', `/v1/platform/organizations/${encode(id)}`);
  }

  updateOrganization(
    id: string,
    input: { display_name?: string; status?: string },
  ): Promise<RequestResult<OrganizationResource>> {
    return this.#request<OrganizationResource>(
      'PATCH',
      `/v1/platform/organizations/${encode(id)}`,
      input,
    );
  }

  getOrganization(id: string): Promise<RequestResult<OrganizationResource>> {
    return this.#request<OrganizationResource>('GET', `/v1/organizations/${encode(id)}`);
  }

  listMembers(organizationId: string): Promise<RequestResult<Page<MemberResource>>> {
    return this.#request<Page<MemberResource>>(
      'GET',
      `/v1/organizations/${encode(organizationId)}/members`,
    );
  }

  listInvitations(organizationId: string): Promise<RequestResult<Page<InvitationResource>>> {
    return this.#request<Page<InvitationResource>>(
      'GET',
      `/v1/organizations/${encode(organizationId)}/invitations`,
    );
  }

  inviteMember(
    organizationId: string,
    input: { email: string; role: string },
  ): Promise<RequestResult<InvitationResource>> {
    return this.#request<InvitationResource>(
      'POST',
      `/v1/organizations/${encode(organizationId)}/invitations`,
      input,
    );
  }

  revokeInvitation(
    organizationId: string,
    invitationId: string,
  ): Promise<RequestResult<InvitationResource>> {
    return this.#request<InvitationResource>(
      'POST',
      `/v1/organizations/${encode(organizationId)}/invitations/${encode(invitationId)}/revoke`,
    );
  }

  changeMemberRole(
    organizationId: string,
    membershipId: string,
    role: string,
  ): Promise<RequestResult<MemberResource>> {
    return this.#request<MemberResource>(
      'PATCH',
      `/v1/organizations/${encode(organizationId)}/members/${encode(membershipId)}`,
      { role },
    );
  }

  setMemberStatus(
    organizationId: string,
    membershipId: string,
    intent: 'suspend' | 'reactivate' | 'remove',
  ): Promise<RequestResult<MemberResource>> {
    return this.#request<MemberResource>(
      'POST',
      `/v1/organizations/${encode(organizationId)}/members/${encode(membershipId)}/${intent}`,
    );
  }

  seedOrganizationOwner(
    organizationId: string,
    email: string,
  ): Promise<RequestResult<{ outcome: string; email: string }>> {
    return this.#request('POST', `/v1/platform/organizations/${encode(organizationId)}/owner`, {
      email,
    });
  }

  /* --------------------------------- catalog ------------------------------- */

  listProducts(organizationId: string): Promise<RequestResult<Page<ProductResource>>> {
    return this.#request('GET', `/v1/organizations/${encode(organizationId)}/products`);
  }

  createProduct(
    organizationId: string,
    input: { name: string; distribution: string; slug?: string; description?: string | null },
  ): Promise<RequestResult<ProductResource>> {
    return this.#request('POST', `/v1/organizations/${encode(organizationId)}/products`, input);
  }

  getProduct(organizationId: string, productId: string): Promise<RequestResult<ProductResource>> {
    return this.#request(
      'GET',
      `/v1/organizations/${encode(organizationId)}/products/${encode(productId)}`,
    );
  }

  updateProduct(
    organizationId: string,
    productId: string,
    input: {
      /** Required. The version the edit was written against (428 without it). */
      expected_version: number;
      name?: string;
      description?: string | null;
      distribution?: string;
    },
  ): Promise<RequestResult<ProductResource>> {
    return this.#request(
      'PATCH',
      `/v1/organizations/${encode(organizationId)}/products/${encode(productId)}`,
      input,
    );
  }

  archiveProduct(
    organizationId: string,
    productId: string,
    expectedVersion: number,
  ): Promise<RequestResult<ProductResource>> {
    return this.#request(
      'POST',
      `/v1/organizations/${encode(organizationId)}/products/${encode(productId)}/archive`,
      { expected_version: expectedVersion },
    );
  }

  /* ---------------------------- environments ------------------------------- */

  /*
    Every path names the product. An environment id alone would resolve one
    belonging to a different product of the same organization, so the parent is
    a predicate rather than a breadcrumb.
  */
  #environments(organizationId: string, productId: string): string {
    return `/v1/organizations/${encode(organizationId)}/products/${encode(productId)}/environments`;
  }

  listEnvironments(
    organizationId: string,
    productId: string,
  ): Promise<RequestResult<Page<EnvironmentResource>>> {
    return this.#request<Page<EnvironmentResource>>(
      'GET',
      this.#environments(organizationId, productId),
    );
  }

  createEnvironment(
    organizationId: string,
    productId: string,
    input: { name: string; kind: string; classification: string; slug?: string },
  ): Promise<RequestResult<EnvironmentResource>> {
    return this.#request<EnvironmentResource>(
      'POST',
      this.#environments(organizationId, productId),
      input,
    );
  }

  updateEnvironment(
    organizationId: string,
    productId: string,
    environmentId: string,
    input: {
      expected_version: number;
      name?: string;
      kind?: string;
      classification?: string;
    },
  ): Promise<RequestResult<EnvironmentResource>> {
    return this.#request<EnvironmentResource>(
      'PATCH',
      `${this.#environments(organizationId, productId)}/${encode(environmentId)}`,
      input,
    );
  }

  archiveEnvironment(
    organizationId: string,
    productId: string,
    environmentId: string,
    expectedVersion: number,
  ): Promise<RequestResult<EnvironmentResource>> {
    return this.#request<EnvironmentResource>(
      'POST',
      `${this.#environments(organizationId, productId)}/${encode(environmentId)}/archive`,
      { expected_version: expectedVersion },
    );
  }

  /* ---------------------------- catalog summary ------------------------------ */

  /**
   * Organization-wide catalog totals, counted by the server.
   *
   * Deliberately not derived from the products list: that only ever sees one
   * page, so any total computed from it is right until a vendor is successful.
   */
  getCatalogSummary(organizationId: string): Promise<RequestResult<CatalogSummaryResource>> {
    return this.#request<CatalogSummaryResource>(
      'GET',
      `/v1/organizations/${encode(organizationId)}/catalog-summary`,
    );
  }

  /* -------------------------------- customers -------------------------------- */

  #customers(organizationId: string): string {
    return `/v1/organizations/${encode(organizationId)}/customers`;
  }

  listCustomers(organizationId: string): Promise<RequestResult<Page<CustomerResource>>> {
    return this.#request<Page<CustomerResource>>('GET', this.#customers(organizationId));
  }

  createCustomer(
    organizationId: string,
    input: {
      name: string;
      contact_email?: string | null;
      external_reference?: string | null;
    },
  ): Promise<RequestResult<CustomerResource>> {
    return this.#request<CustomerResource>('POST', this.#customers(organizationId), input);
  }

  updateCustomer(
    organizationId: string,
    customerId: string,
    input: {
      expected_version: number;
      name?: string;
      contact_email?: string | null;
      external_reference?: string | null;
    },
  ): Promise<RequestResult<CustomerResource>> {
    return this.#request<CustomerResource>(
      'PATCH',
      `${this.#customers(organizationId)}/${encode(customerId)}`,
      input,
    );
  }

  archiveCustomer(
    organizationId: string,
    customerId: string,
    expectedVersion: number,
  ): Promise<RequestResult<CustomerResource>> {
    return this.#request<CustomerResource>(
      'POST',
      `${this.#customers(organizationId)}/${encode(customerId)}/archive`,
      { expected_version: expectedVersion },
    );
  }

  /* ------------------------ licensed product plans -------------------------- */

  #plans(organizationId: string, productId: string): string {
    return `/v1/organizations/${encode(organizationId)}/products/${encode(productId)}/plans`;
  }

  listLicensedProductPlans(
    organizationId: string,
    productId: string,
  ): Promise<RequestResult<Page<LicensedProductPlanResource>>> {
    return this.#request<Page<LicensedProductPlanResource>>(
      'GET',
      this.#plans(organizationId, productId),
    );
  }

  createLicensedProductPlan(
    organizationId: string,
    productId: string,
    input: {
      name: string;
      code?: string;
      description?: string | null;
      list_price_minor?: number | null;
      currency?: string | null;
      billing_interval?: string | null;
    },
  ): Promise<RequestResult<LicensedProductPlanResource>> {
    return this.#request<LicensedProductPlanResource>(
      'POST',
      this.#plans(organizationId, productId),
      input,
    );
  }

  updateLicensedProductPlan(
    organizationId: string,
    productId: string,
    planId: string,
    input: {
      expected_version: number;
      name?: string;
      description?: string | null;
      list_price_minor?: number | null;
      currency?: string | null;
      billing_interval?: string | null;
    },
  ): Promise<RequestResult<LicensedProductPlanResource>> {
    return this.#request<LicensedProductPlanResource>(
      'PATCH',
      `${this.#plans(organizationId, productId)}/${encode(planId)}`,
      input,
    );
  }

  activateLicensedProductPlan(
    organizationId: string,
    productId: string,
    planId: string,
    expectedVersion: number,
  ): Promise<RequestResult<LicensedProductPlanResource>> {
    return this.#request<LicensedProductPlanResource>(
      'POST',
      `${this.#plans(organizationId, productId)}/${encode(planId)}/activate`,
      { expected_version: expectedVersion },
    );
  }

  archiveLicensedProductPlan(
    organizationId: string,
    productId: string,
    planId: string,
    expectedVersion: number,
  ): Promise<RequestResult<LicensedProductPlanResource>> {
    return this.#request<LicensedProductPlanResource>(
      'POST',
      `${this.#plans(organizationId, productId)}/${encode(planId)}/archive`,
      { expected_version: expectedVersion },
    );
  }

  listPlatformUsers(): Promise<RequestResult<Page<PlatformUserResource>>> {
    return this.#request<Page<PlatformUserResource>>('GET', '/v1/platform/users');
  }

  setPlatformUserStatus(
    userId: string,
    intent: 'suspend' | 'reactivate',
  ): Promise<RequestResult<PlatformUserResource>> {
    return this.#request<PlatformUserResource>(
      'POST',
      `/v1/platform/users/${encode(userId)}/${intent}`,
    );
  }

  grantPlatformRole(userId: string, role: string): Promise<RequestResult<PlatformUserResource>> {
    return this.#request<PlatformUserResource>(
      'POST',
      `/v1/platform/users/${encode(userId)}/platform-role`,
      { role },
    );
  }

  listPlatformAudit(): Promise<RequestResult<Page<AuditEventResource>>> {
    return this.#request<Page<AuditEventResource>>('GET', '/v1/platform/audit');
  }

  /**
   * @param resource narrows to one resource's own history — a product's, say.
   *   The organization scope still applies; this can only ever return less.
   */
  listOrganizationAudit(
    organizationId: string,
    resource?: { type: string; id: string },
  ): Promise<RequestResult<Page<AuditEventResource>>> {
    const query =
      resource === undefined
        ? ''
        : `?resource_type=${encode(resource.type)}&resource_id=${encode(resource.id)}`;
    return this.#request<Page<AuditEventResource>>(
      'GET',
      `/v1/organizations/${encode(organizationId)}/audit${query}`,
    );
  }

  /**
   * Escape hatch for endpoints the typed methods do not cover yet.
   *
   * Public so the console is never tempted to reach around the SDK with a raw
   * fetch — which is how request-id capture and Problem Details decoding get
   * quietly lost on exactly the calls that need them most.
   */
  request<T>(method: string, path: string, body?: unknown): Promise<RequestResult<T>> {
    return this.#request<T>(method, path, body);
  }

  async #request<T>(method: string, path: string, body?: unknown): Promise<RequestResult<T>> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= this.#maxRetries; attempt += 1) {
      if (attempt > 0) {
        // Exponential backoff with full jitter. Without jitter, every client that
        // failed together retries together and the recovering service is knocked
        // over by its own clients.
        const backoffMs = Math.min(2 ** attempt * 200, 4_000);
        await this.#sleep(Math.random() * backoffMs);
      }

      try {
        return await this.#attempt<T>(method, path, body);
      } catch (error) {
        lastError = error;
        // Only safe methods are retried. Replaying a POST that already reached
        // the server would send a second invitation, or create a second
        // organization, on a connection error that happened after it was
        // processed.
        const safe = method === 'GET' || method === 'HEAD';
        const retryable =
          safe &&
          (error instanceof VerydiaConnectionError ||
            (error instanceof VerydiaApiError && error.isRetryable));
        if (!retryable) throw error;
      }
    }

    throw lastError;
  }

  async #attempt<T>(method: string, path: string, body?: unknown): Promise<RequestResult<T>> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      'user-agent': this.#userAgent,
    };

    if (this.#apiKey) headers.authorization = `Bearer ${this.#apiKey}`;
    if (this.#authorization) {
      const token = await this.#authorization();
      if (token !== null) headers.authorization = `Bearer ${token}`;
    }
    if (body !== undefined) headers['content-type'] = 'application/json';

    let response: Response;
    try {
      response = await this.#fetch(`${this.#baseUrl}${path}`, {
        method,
        headers,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: AbortSignal.timeout(this.#timeoutMs),
      });
    } catch (error) {
      throw new VerydiaConnectionError(`Could not reach ${this.#baseUrl}${path}.`, error);
    }

    const requestId = response.headers.get(REQUEST_ID_HEADER);

    if (!response.ok) {
      const problem = await this.#readProblem(response);
      const retryAfter = Number(response.headers.get('retry-after'));
      throw new VerydiaApiError({
        message: problem?.detail ?? `Request failed with status ${response.status}.`,
        status: response.status,
        code: problem?.code ?? 'unknown_error',
        requestId: problem?.request_id ?? requestId,
        problem,
        // Honouring the server's own number beats guessing one: it is the
        // difference between backing off correctly and hammering a service that
        // is already struggling.
        retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
      });
    }

    // 204 has no body, and calling .json() on one throws.
    if (response.status === 204) {
      return { data: undefined as T, requestId, status: response.status };
    }
    return { data: (await response.json()) as T, requestId, status: response.status };
  }

  async #readProblem(response: Response): Promise<Problem | null> {
    try {
      const body: unknown = await response.json();
      // Duck-typed rather than schema-parsed: a proxy or gateway may return its own
      // error body, and failing to parse it must not mask the real HTTP failure.
      return body !== null && typeof body === 'object' && 'code' in body ? (body as Problem) : null;
    } catch {
      return null;
    }
  }
}
